<template>
  <div :key="model?.click_url" class="product-card__slider-wrapper">
    <div class="product-card__slider-content">
      <div
        v-if="type === 'model' && blockType === 'tile'"
        class="product-card__icons"
      >
        <ui-ui-button-ico
          icon="compare"
          :responsive-ico="true"
          size-ico="28"
          :responsive-sizes="[24, 24, 24, 28, 28, 28]"
          class="product-card__compare-btn"
          type="tertiary"
          :fill="inCompare ? '#363EF8' : 'white'"
          :stroke="inCompare ? '#363EF8' : '#86888F'"
          @click.prevent.stop="toggleCompare"
        />

        <ui-ui-button-ico
          icon="favorites"
          :responsive-ico="true"
          size-ico="28"
          :responsive-sizes="[24, 24, 24, 28, 28, 28]"
          class="product-card__favorite-btn"
          type="tertiary"
          :fill="favorite ? '#363EF8' : 'white'"
          :stroke="favorite ? '#363EF8' : '#86888F'"
          @click.prevent="toggleFavorite"
        />
      </div>
      <component
        :is="selectComponent"
        :to="componentUrl"
        :offer="type !== 'model' && model"
        class="product-card__slider-link"
        :position="type !== 'model' && position"
        :block-type="type !== 'model' && blockType"
        :disabled="disabled"
      >
        <UiResponsiveImage
          v-if="imagesModal?.length < 2 || !isFirstInteractive"
          :src="model.image || '/static/images/no-img.svg'"
          class="product-card__slider--img"
          :lazy="false"
          :loading="undefined"
          :alt="model.title || model?.name || ''"
          :title="model.title || model?.name || ''"
          :itemprop="useSchema ? 'image' : undefined"
          :mobile-sizes="{ width: 120, height: 120 }"
          :desktop-sizes="{ width: 224, height: 224 }"
        />

        <UiSoloSlider
          v-else-if="!isMobile"
          :key="model?.click_url + 'soloSlider'"
          v-model="item"
          sections
          small
          only-five
          :items="imagesModal"
          :lazy-img="false"
          :name="model.title || model.name || ''"
          :itemprop="useSchema"
          :mobile-sizes="{ width: 313, height: 313 }"
          :desktop-sizes="{ width: 313, height: 313 }"
          :use-schema="useSchema"
        />
        <nuxt-error-boundary v-else @error="() => {}">
          <swiper
            class="product-card-swiper"
            :lazy="true"
            :loop="false"
            :modules="[Navigation, Thumbs, Pagination]"
            :grab-cursor="true"
            :space-between="10"
            :pagination="{
              clickable: true,
              dynamicBullets: true,
              dynamicMainBullets: 5,
            }"
          >
            <swiper-slide
              v-for="(item, index) in imagesModal.slice(0, 5)"
              :key="index"
              class="product-card-swiper-slider"
            >
              <UiResponsiveImage
                class="icon"
                :src="item || '/static/images/no-img.svg'"
                :lazy="lazyImg"
                :loading="nuxtLazy ? 'lazy' : undefined"
                draggable="false"
                :adaptive-image="true"
                :mobile-sizes="{ width: 150, height: 150 }"
                :desktop-sizes="{ width: 224, height: 224 }"
                :load-original="false"
                :alt="model.title || model.name || ''"
                :title="model.title || model.name || ''"
                :itemprop="useSchema ? 'image' : undefined"
              />
            </swiper-slide>
          </swiper>
        </nuxt-error-boundary>
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { useCompareStore } from '~/stores/compare';
import { useFavoriteStore } from '~/stores/favorite';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import cpcLink from '~/components/ui/CpcLink.vue';
import { useTimeCalculation } from '~/composition/useTimeCalculation';
import { useInteractiveStore } from '~/stores/interactive';

const props = defineProps({
  model: {
    type: Object as () => any,
  },
  offers: Array,
  lazyImg: {
    type: Boolean,
    default: false,
  },
  nuxtLazy: {
    type: Boolean,
    default: false,
  },
  useSchema: Boolean,
  index: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: 'model',
  },
  blockType: {
    type: String,
    default: 'listing',
  },
  modelPath: {
    type: String,
  },
  position: {
    type: Number,
    default: 1,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const item = ref<Number>(0);
const compareStore = useCompareStore();
const { $ymAnyEvent } = useNuxtApp();
const favoriteStore = useFavoriteStore();
const mediaStore = useMediaQueryStore();
const interactiveStore = useInteractiveStore();
const isFirstInteractive = computed(() => interactiveStore.isFirstInteractive);
const isMobile = computed(
  () =>
    mediaStore.isMobile || mediaStore.isMd || mediaStore.isSm || mediaStore.isEs
);

const inCompare = computed(
  () =>
    props.model &&
    compareStore.ids.find(
      (id: number) => Number(id) === Number(props.model?.id)
    )
);
const favorite = computed(
  () =>
    props.model &&
    favoriteStore.ids.find(
      (id: number) => Number(id) === Number(props.model?.id)
    )
);
const isPlaceOrder = computed(() => props.model?.cpo);
const componentUrl = computed(() => {
  if (props.type !== 'model') {
    return isPlaceOrder.value || props.disabled
      ? undefined
      : props.model.click_url;
  }
  return props.modelPath;
});
const toggleFavorite = () => {
  if (props.model)
    favoriteStore.toggleFavorite({
      id: props.model.id,
      catId: +props.model.category.id,
      deleteCard: !!favorite.value,
    });
};
const toggleCompare = () => {
  if (props.model) {
    compareStore.toggleCompare({
      id: props.model.id,
      catId: +props.model.category.id,
      deleteCard: !!inCompare.value,
    });
  }
  if (inCompare.value) {
    $ymAnyEvent('reachGoal', 'Compare');
  }
};
const imagesModal = computed(() => {
  if (props.model)
    return props.model.additional_images?.length
      ? [props.model.image, ...props.model.additional_images]
      : [props.model.image];
  return [];
});
const selectComponent = computed(() => {
  if (props.type === 'model') return defineNuxtLink({});
  if (props.disabled) return 'div';
  return cpcLink;
});
useTimeCalculation();
</script>

<style scoped lang="scss">
.product-card__compare-btn,
.product-card__favorite-btn {
  position: absolute;
  z-index: 10;
}

.product-card__slider-wrapper {
  overflow: hidden;
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 100%;

  .product-card__slider-content {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    min-width: 100%;
    min-height: 100%;
    max-width: 100%;
    max-height: 100%;
    /* это свайпер для мобил: */
    .product-card-swiper {
      height: 100%;
      width: 100%;

      :deep(.swiper-pagination) {
        top: auto;
        bottom: 0;
        left: unset;
        right: 0;
        transform: none;
        width: 40px !important;

        .swiper-pagination-bullet {
          width: 4px;
          height: 4px;
          transform: none !important;
          margin: 0 2px;
          background: #d3d3d3;
          opacity: 1;

          &.swiper-pagination-bullet-active {
            background: #363ef8;
          }
        }

        @include xl-block {
          .swiper-pagination-bullet {
            width: 6px;
            height: 6px;
            visibility: hidden;
          }
        }
      }
    }

    /* это соло-сдайдер для десктопов: */
    .slider.small {
      height: 100%;

      :deep(.swiper-container) {
        height: 100%;
        padding: 0;
        mix-blend-mode: multiply;

        .swiper-pagination-bullets {
          bottom: 0;
          font-size: 0;
          line-height: 0;
          left: auto;
          right: 0;
          width: 40px;
        }
      }
    }
  }
}

.product-card__icons {
  position: absolute;
  right: 12px;
  width: 28px;
  z-index: 3;
  height: 100%;
  @include lg-block {
    right: 16px;
  }

  .product-card__compare-btn {
    top: 40px;
    @include lg-block {
      top: 44px;
    }
  }

  .product-card__favorite-btn {
    top: 0;
  }
}

.product-card__slider-link {
  display: block;
  height: 100%;
  mix-blend-mode: multiply;

  .product-card__slider--img {
    object-fit: contain;
    object-position: center;
    width: 100%;
    height: 100%;
  }

  .slider {
    :deep(.swiper-wrapper) {
      .adaptive-image {
        mix-blend-mode: multiply;
      }
    }
  }
}

.swiper-pagination {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  bottom: 0;
  @include xl-block {
    bottom: -6px;
  }
}

:deep(.swiper-pagination) {
  left: auto !important;
  right: 0 !important;
  transform: translateX(0) !important;
  width: min-content !important;
}

:deep(.swiper-pagination-bullet) {
  width: 6px;
  height: 6px;
  margin: 0 2px;
  opacity: 1;
  background: $achromatic-dots;
}

:deep(.swiper-pagination-bullet-active) {
  background: $main-cta;
}
</style>
